import { createContext, useContext, useState } from "react";

const CartContext = createContext(null);

/**
 * CartProvider exposes:
 *   items          – [{ cartItemId, product, variant, quantity }]
 *   addToCart      – (product, variant, quantity) => void
 *   removeFromCart – (cartItemId) => void
 *   updateQty      – (cartItemId, newQty) => void
 *   clearCart      – () => void
 *   totalItems     – total unit count shown on the cart badge
 *   totalPrice     – formatted dollar string for the order summary
 */
export function CartProvider({ children }) {
  const [items, setItems] = useState([]);

  const addToCart = (product, variant, quantity) => {
    setItems((prev) => {
      // Same product + same variant → bump quantity on existing line
      const idx = prev.findIndex(
        (item) => item.product.id === product.id && item.variant === variant
      );
      if (idx !== -1) {
        const updated = [...prev];
        updated[idx] = { ...updated[idx], quantity: updated[idx].quantity + quantity };
        return updated;
      }
      return [
        ...prev,
        { cartItemId: `${product.id}-${variant}-${Date.now()}`, product, variant, quantity },
      ];
    });
  };

  const removeFromCart = (cartItemId) =>
    setItems((prev) => prev.filter((item) => item.cartItemId !== cartItemId));

  const updateQty = (cartItemId, newQty) => {
    if (newQty < 1) return;
    setItems((prev) =>
      prev.map((item) =>
        item.cartItemId === cartItemId ? { ...item, quantity: newQty } : item
      )
    );
  };

  const clearCart = () => setItems([]);

  const totalItems = items.reduce((sum, item) => sum + item.quantity, 0);

  const totalPrice = items
    .reduce((sum, item) => sum + (item.product.priceNum ?? 0) * item.quantity, 0)
    .toFixed(2);

  return (
    <CartContext.Provider
      value={{ items, addToCart, removeFromCart, updateQty, clearCart, totalItems, totalPrice }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside <CartProvider>");
  return ctx;
}
